/**
 * Matrice de permissions du flux « séances d'essai » et réservations staff.
 * Source de vérité côté FRONT (l'edge trial-prospects / bookings-management
 * applique les mêmes règles côté serveur) :
 * - l'admin a tout ;
 * - le coach gère le cycle de vie commercial (saisie, replanification,
 *   conversion) et peut réserver pour un tiers, mais ne touche JAMAIS à
 *   l'argent (choix d'abonnement à la conversion) ni à la suppression ;
 * - tout autre rôle n'a rien.
 */

export type StaffRole = "admin" | "coach" | "athlete" | "super_admin" | string;

// Aligné sur le backend : `checkRole` (edge _shared/auth.ts) ne reconnaît que
// le rôle exact — un `super_admin` sans ligne `admin` sur la salle recevrait
// 403 sur toutes les actions ; ne pas lui montrer les boutons.
const isAdmin = (role: StaffRole) => role === "admin";
const isStaff = (role: StaffRole) => isAdmin(role) || role === "coach";

/** Supprimer un prospect (perte de trace commerciale) : admin uniquement. */
export const canDeleteProspect = (role: StaffRole) => isAdmin(role);

/**
 * Adjoindre un abonnement à la conversion : admin ET coach.
 * Décision produit : le coach a la main complète sur la conversion d'un
 * prospect, y compris le choix de l'abonnement (l'edge accepte les deux rôles
 * et journalise l'auteur dans `recent_activities`).
 */
export const canChooseSubscriptionOnConvert = (role: StaffRole) => isStaff(role);

/**
 * Réserver un cours pour un tiers : ADMIN uniquement.
 * Décision produit (réaffirmée) : le coach ne réserve JAMAIS pour un adhérent
 * et n'a pas accès à la liste des adhérents depuis le dialogue de réservation.
 */
export const canBookForOthers = (role: StaffRole) => isAdmin(role);

/** Actions du cycle de vie d'un essai visibles pour un rôle donné. */
export const visibleTrialActions = (role: StaffRole) => ({
  create: isStaff(role),
  update: isStaff(role),
  updateStatus: isStaff(role),
  convert: isStaff(role),
  cancel: isStaff(role),
  reschedule: isStaff(role),
  remove: canDeleteProspect(role),
});
